import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store';
import { UserPrivilegeEnum } from '../../../../enums/rights/user-privilege.enum';
import { RChecklistTemplate } from './r-checklist-templat.action';
import { ResetStateDefaults } from '../../../base-action-class.model';
import { RightsScopeEnum } from '../../../../enums/rights/rights-scope.enum';

interface RChecklistTemplateXsStateModel {

  // organization
  viewOrganizationChecklistTemplate: string;
  createOrganizationChecklistTemplate: string;
  editOrganizationChecklistTemplate: string;
  deleteOrganizationChecklistTemplate: string;

  // community
  viewCommunityChecklistTemplate: string;
  createCommunityChecklistTemplate: string;
  editCommunityChecklistTemplate: string;
  deleteCommunityChecklistTemplate: string;

  //service provider
  viewServiceProviderChecklistTemplate: string;
  createServiceProviderChecklistTemplate: string;
  editServiceProviderChecklistTemplate: string;
  deleteServiceProviderChecklistTemplate: string;

  // global
  viewGlobalChecklistTemplate: string;
  createGlobalChecklistTemplate: string;
  editGlobalChecklistTemplate: string;
  deleteGlobalChecklistTemplate: string;
}

const getDefaults = (): RChecklistTemplateXsStateModel => ({
  viewOrganizationChecklistTemplate: null,
  createOrganizationChecklistTemplate: null,
  editOrganizationChecklistTemplate: null,
  deleteOrganizationChecklistTemplate: null,

  viewCommunityChecklistTemplate: null,
  createCommunityChecklistTemplate: null,
  editCommunityChecklistTemplate: null,
  deleteCommunityChecklistTemplate: null,

  //service provider
  viewServiceProviderChecklistTemplate: null,
  createServiceProviderChecklistTemplate: null,
  editServiceProviderChecklistTemplate: null,
  deleteServiceProviderChecklistTemplate: null,

  // global
  viewGlobalChecklistTemplate: null,
  createGlobalChecklistTemplate: null,
  editGlobalChecklistTemplate: null,
  deleteGlobalChecklistTemplate: null
});


@State<RChecklistTemplateXsStateModel>({
  name: 'rChecklistTemplate',
  defaults: getDefaults(),
})
@Injectable()
export class RChecklistTemplateXsState {

  // region SELECTOR
  @Selector()
  static canCreateAnyChecklistTemplate(state: RChecklistTemplateXsStateModel): boolean {
    return state.createOrganizationChecklistTemplate != null ||
      state.createCommunityChecklistTemplate != null ||
      state.createServiceProviderChecklistTemplate != null ||
      state.createGlobalChecklistTemplate != null;
  }

  static canEditChecklistTemplate(scope: RightsScopeEnum) {
    return createSelector([RChecklistTemplateXsState], (state: RChecklistTemplateXsStateModel): boolean => {
      switch (scope) {
        case RightsScopeEnum.GLOBAL:
          return state.editGlobalChecklistTemplate != null;
        case RightsScopeEnum.SERVICE_PROVIDER:
          return state.editServiceProviderChecklistTemplate != null;
        case RightsScopeEnum.COMMUNITY:
          return state.editCommunityChecklistTemplate != null;
        case RightsScopeEnum.ORGANIZATION:
          return state.editOrganizationChecklistTemplate != null;
      }
      return false;
    });
  }
  // endregion

  // region ACTIONS
  @Action(RChecklistTemplate.SetChecklistTemplatePrivileges)
  public setChecklistTemplatePrivileges(ctx: StateContext<RChecklistTemplateXsStateModel>, action: RChecklistTemplate.SetChecklistTemplatePrivileges) {
    const state = ctx.getState();
    const checklistPrivileges = [...action.activeUserRightInfoDtoModel.checklistTemplatePrivileges];

    // find the user privileges
    const viewOrganizationChecklistTemplate = checklistPrivileges.find(id => id === UserPrivilegeEnum.VIEW_ORG_CHECKLIST_TEMPLATE);
    const createOrganizationChecklistTemplate = checklistPrivileges.find(id => id === UserPrivilegeEnum.CREATE_ORG_CHECKLIST_TEMPLATE);
    const editOrganizationChecklistTemplate = checklistPrivileges.find(id => id === UserPrivilegeEnum.EDIT_ORG_CHECKLIST_TEMPLATE);
    const deleteOrganizationChecklistTemplate = checklistPrivileges.find(id => id === UserPrivilegeEnum.DELETE_ORG_CHECKLIST_TEMPLATE);

    const viewCommunityChecklistTemplate = checklistPrivileges.find(id => id === UserPrivilegeEnum.VIEW_COMMUNITY_CHECKLIST_TEMPLATE);
    const createCommunityChecklistTemplate = checklistPrivileges.find(id => id === UserPrivilegeEnum.CREATE_COMMUNITY_CHECKLIST_TEMPLATE);
    const editCommunityChecklistTemplate = checklistPrivileges.find(id => id === UserPrivilegeEnum.EDIT_COMMUNITY_CHECKLIST_TEMPLATE);
    const deleteCommunityChecklistTemplate = checklistPrivileges.find(id => id === UserPrivilegeEnum.DELETE_COMMUNITY_CHECKLIST_TEMPLATE);

    const viewServiceProviderChecklistTemplate = checklistPrivileges.find(id => id === UserPrivilegeEnum.VIEW_SERVICE_PROVIDER_CHECKLIST);
    const createServiceProviderChecklistTemplate = checklistPrivileges.find(id => id === UserPrivilegeEnum.CREATE_SERVICE_PROVIDER_CHECKLIST);
    const editServiceProviderChecklistTemplate = checklistPrivileges.find(id => id === UserPrivilegeEnum.EDIT_SERVICE_PROVIDER_CHECKLIST);
    const deleteServiceProviderChecklistTemplate = checklistPrivileges.find(id => id === UserPrivilegeEnum.DELETE_SERVICE_PROVIDER_CHECKLIST);

    const viewGlobalChecklistTemplate = checklistPrivileges.find(id => id === UserPrivilegeEnum.VIEW_GLOBAL_CHECKLIST_TEMPLATE);
    const createGlobalChecklistTemplate = checklistPrivileges.find(id => id === UserPrivilegeEnum.CREATE_GLOBAL_CHECKLIST_TEMPLATE);
    const editGlobalChecklistTemplate = checklistPrivileges.find(id => id === UserPrivilegeEnum.EDIT_GLOBAL_CHECKLIST_TEMPLATE);
    const deleteGlobalChecklistTemplate = checklistPrivileges.find(id => id === UserPrivilegeEnum.DELETE_GLOBAL_CHECKLIST_TEMPLATE);

    ctx.setState({
      ...state,
      viewOrganizationChecklistTemplate: viewOrganizationChecklistTemplate,
      createOrganizationChecklistTemplate: createOrganizationChecklistTemplate,
      editOrganizationChecklistTemplate: editOrganizationChecklistTemplate,
      deleteOrganizationChecklistTemplate: deleteOrganizationChecklistTemplate,

      viewCommunityChecklistTemplate: viewCommunityChecklistTemplate,
      createCommunityChecklistTemplate: createCommunityChecklistTemplate,
      editCommunityChecklistTemplate: editCommunityChecklistTemplate,
      deleteCommunityChecklistTemplate: deleteCommunityChecklistTemplate,

      viewServiceProviderChecklistTemplate: viewServiceProviderChecklistTemplate,
      createServiceProviderChecklistTemplate: createServiceProviderChecklistTemplate,
      editServiceProviderChecklistTemplate: editServiceProviderChecklistTemplate,
      deleteServiceProviderChecklistTemplate: deleteServiceProviderChecklistTemplate,

      viewGlobalChecklistTemplate: viewGlobalChecklistTemplate,
      createGlobalChecklistTemplate: createGlobalChecklistTemplate,
      editGlobalChecklistTemplate: editGlobalChecklistTemplate,
      deleteGlobalChecklistTemplate: deleteGlobalChecklistTemplate
    });
  }

  @Action(ResetStateDefaults)
  public resetDefault(ctx: StateContext<RChecklistTemplateXsStateModel>): void {
    ctx.setState({ ...getDefaults() });
  }
  // endregion
}
